import { useFormik } from "formik"
import { Button, CurrencyInput, Grid, Input, NumericInput, Switch, Typography } from "../../../components"
import currency from 'currency.js'
import { formatMoney, formatNumber, nl2br } from "@bussola-viagens/utils"
import { useContext, useMemo } from "react"
import {PriceSetting} from '@bussola-viagens/constants'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { AuthContext } from "../../../contexts"
import { Alert, AlertTitle, Divider } from "@mui/material"

interface TudoAzulV2CalculatorProps {
    configuration: {
        prices: PriceSetting[],
        flight_in_next_seven_days: number
        comments?: string
    },
    costs: PriceSetting[]
}

export const TudoAzulV2Calculator = ({ configuration, costs }: TudoAzulV2CalculatorProps) => {
    const {me} = useContext(AuthContext)

    const formik = useFormik({
        initialValues: {
            pax: 1,
            miles: '',
            fees: '',
            extra: '',
            fly_this_week: false
        },
        onSubmit: () => {}
    })

    const kPerPax = formik.values.pax ? currency(Number(formik.values.miles)).divide(1000).divide(Number(formik.values.pax)).value : 1

    const percentPerK = useMemo(() => {
            if (formik.values.fly_this_week) {
                return configuration.flight_in_next_seven_days
            }
    
            const price = configuration.prices.find(p => {
                if (!p.to && currency(Number(p.from)).divide(1000).value <= kPerPax) {
                    return true
                }
    
                return currency(Number(p.from)).divide(1000).value <= kPerPax && currency(Number(p.to)).divide(1000).value >= kPerPax
            })
    
            return price?.price || 0
        }, [formik.values.fly_this_week, configuration.flight_in_next_seven_days, configuration.prices, kPerPax])
    
const costPerK = useMemo(() => {
        const cost = costs.find(p => {
            if (!p.to && currency(Number(p.from)).divide(1000).value <= kPerPax) {
                return true
            }

            return currency(Number(p.from)).divide(1000).value <= kPerPax && currency(Number(p.to)).divide(1000).value >= kPerPax
        })

        return cost?.price || 0
    }, [configuration.flight_in_next_seven_days, configuration.prices, kPerPax, formik.values.fly_this_week, formik.values.miles, formik.values.pax, costs])
    
    const pricePerK = useMemo(() => {
        return currency(costPerK).multiply(currency(1).add(currency(percentPerK).divide(100))).value
    }, [percentPerK, costPerK])

    const fees = useMemo(() => currency(formik.values.fees).multiply(0.95).value, [formik.values.fees])

    const totalCost = useMemo(() => {
        const k = currency(formik.values.miles).divide(1000).value
        const cost = currency(k).multiply(costPerK).value

        return currency(cost).add(fees || 0).add(formik.values.extra || 0).value
    }, [pricePerK, formik.values.extra, fees, formik.values.miles])
    
    const totalPrice = useMemo(() => {
        const k = currency(formik.values.miles).divide(1000).value
        const cost = currency(k).multiply(pricePerK).value

        return currency(cost).add(fees || 0).add(formik.values.extra || 0).value
    }, [pricePerK, formik.values.extra, fees, formik.values.miles])



    // const costPerK = useMemo(() => {
    //     if (formik.values.fly_this_week) {
    //         return configuration.flight_in_next_seven_days
    //     }

    //     const kPerPax = formik.values.pax ? currency(Number(formik.values.miles)).divide(1000).divide(Number(formik.values.pax)).value : 1
        
    //     const price = configuration.prices.find(p => {
    //         if (!p.to && currency(Number(p.from)).divide(1000).value <= kPerPax) {
    //             return true
    //         }

    //         return currency(Number(p.from)).divide(1000).value <= kPerPax && currency(Number(p.to)).divide(1000).value >= kPerPax
    //     })

    //     return price?.price || 0
    // }, [configuration.flight_in_next_seven_days, configuration.prices, formik.values.fly_this_week, formik.values.miles, formik.values.pax])
    
    // const totalCost = useMemo(() => {
    //     const k = currency(formik.values.miles).divide(1000).value
    //     const cost = currency(k).multiply(costPerK).value

    //     const fees = currency(formik.values.fees).multiply(0.95).value

    //     return currency(cost).add(fees || 0).add(formik.values.extra || 0).value
    // }, [costPerK, formik.values.extra, formik.values.fees, formik.values.miles])

    return (
        <Grid container spacing={1}>
        {configuration.comments && (<Grid item xs={12} mb={2}>
            <Alert icon={false} color="info">
                <AlertTitle>Observações:</AlertTitle>
                <span dangerouslySetInnerHTML={{__html: nl2br(configuration.comments)}} />
            </Alert>
        </Grid>)}
            <Grid item xs={12}>
                <Input 
                    name='pax'
                    label="Passageiros"
                    onChange={(event) => {
                        formik.setFieldValue('pax', event.target.value.replace(/\D/ig, ''))
                    }}
                    value={formik.values.pax}
                />
            </Grid>
            <Grid item xs={12}>
                <NumericInput 
                    name='miles'
                    label="Milhas"
                    onChange={formik.handleChange}
                    value={formik.values.miles}
                />
            </Grid>
            <Grid item xs={12}>
                <CurrencyInput
                    label="Taxas (R$)"
                    onChangeValue={(_, originalValue) => {
                        formik.setFieldValue('fees', originalValue, true)
                    }}
                    value={formik.values.fees}
                    helperText='Todo valor inserido neste campo terá 5% de desconto.'
                />
            </Grid>
            <Grid item xs={12}>
                <CurrencyInput
                    label="Extras (R$)"
                    onChangeValue={(_, originalValue) => {
                        formik.setFieldValue('extra', originalValue, true)
                    }}
                    value={formik.values.extra}
                    helperText='Utilize este campo para custos extras como bagagens e marcação de assentos.'
                />
            </Grid>
            {configuration.flight_in_next_seven_days ? (<Grid item xs={12}>
                <Switch 
                    label="O voo é dentro dos próximos 7 dias?"
                    onChange={() => {
                        formik.setFieldValue('fly_this_week', !formik.values.fly_this_week)
                    }}
                    value={formik.values.fly_this_week}
                />
            </Grid>) : null}

            <Grid item xs={12} mt={2} display={"flex"} justifyContent={"space-evenly"}>
                <Typography textAlign="center" variant="body1" component={"span"}>
                    <strong>Custo:</strong> {formatMoney(costPerK)}
                </Typography>
                <Typography textAlign="center" variant="body1" component={"span"}>
                    <strong>Margem:</strong> {formatNumber(percentPerK)}%
                </Typography>
            </Grid>
            <Grid item xs={12} mt={2}>
                <Typography textAlign="center">
                    <strong>Rentabilidade:</strong> ({currency(formik.values.miles).divide(1000).value} x {formatMoney(pricePerK)}) - ({currency(formik.values.miles).divide(1000).value} x {formatMoney(costPerK)}) = <strong>{formatMoney(currency(totalPrice).subtract(totalCost).value)}</strong>
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Divider />
            </Grid>
            <Grid item xs={12}>
                <Typography textAlign="center">
                    <strong>Total:</strong> ({currency(formik.values.miles).divide(1000).value} x {formatMoney(pricePerK)}) {fees ? `+ ${formatMoney(Number(fees))}` : ''} {formik.values.extra ? `+ ${formatMoney(Number(formik.values.extra))}` : ''} = <strong>{formatMoney(totalPrice)}</strong>
                </Typography>
            </Grid>

            {me?.group.group_module?.is_offer_enabled && (<Grid item xs={12} mt={4} textAlign="center">
                <Button 
                    startIcon={<ReceiptLongIcon/>}
                    href={`/meus-orcamentos/novo?flight_price=${totalPrice}`}
                >
                    Gerar Orçamento 
                </Button>
            </Grid>)}
        </Grid>
    )
}